import axios from 'axios';
import {
  ASSIGN_QUESTIONS_TO_SECTION_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  fetch_questions_in_project,
  unassign_question_from_section,
} from '@/services/question_service';
import { extract_error_message } from '@/utils/api_response_helpers';

function getFirstAvailableValue(values) {
  const resolved_value = values.find((value) => value != null && value !== '') ?? null;
  return resolved_value;
}

function resolveQuestionRowId(question, index) {
  const question_row_id = Number(question?.id ?? question?.question_id ?? index + 1);
  return question_row_id;
}

function resolveQuestionText(question) {
  const question_text = String(question?.question_text || question?.question || 'No question text').trim();
  return question_text;
}

function resolveQuestionTypeLabel(ctx, question) {
  const question_type = getFirstAvailableValue([
    question?.question_type?.question_type,
    question?.question_type?.type,
    question?.question_type,
  ]) || '';
  const question_type_label = ctx.format_question_type_label(question_type);
  return question_type_label;
}

function getSectionQuestionIdCandidates(question) {
  return [
    question?.section_question_id,
    question?.section_question?.id,
    question?.form_section_question_id,
    question?.section_question_mapping_id,
  ];
}

export function resolveSectionQuestionId(question) {
  return Number(getFirstAvailableValue(getSectionQuestionIdCandidates(question)));
}

export function buildAssignedQuestionIds(section, resolveQuestionId) {
  const assigned_question_ids = new Set(
    (Array.isArray(section?.questions) ? section.questions : []).map((question) => {
      const question_id = resolveQuestionId(question);
      return question_id;
    })
  );
  return assigned_question_ids;
}

export function buildQuestionRow(ctx, question, index, assigned_question_ids) {
  const question_id = resolveQuestionRowId(question, index);
  const is_dependent = ctx.is_dependent_question(question);
  const question_row = {
    id: question_id,
    question_id: question_id,
    question_text: resolveQuestionText(question),
    question_type_label: resolveQuestionTypeLabel(ctx, question),
    question_number: String(question?.question_number ?? question_id ?? ''),
    is_unique: Boolean(question?.is_unique),
    is_assigned: assigned_question_ids.has(question_id),
    is_dependent: is_dependent,
    assignment_label: is_dependent ? 'Dependent' : 'Available',
    is_saving: false,
  };
  return question_row;
}

export function normalizeAssignmentFallback(question) {
  const fallback = question.is_assigned
    ? 'Failed to unassign question from section.'
    : 'Failed to assign questions to section.';
  return fallback;
}

export function hasAssignmentContext(section, section_id, form_id) {
  const has_context = Boolean(section && section_id && form_id);
  return has_context;
}

export async function loadAssignableQuestions(ctx, form_id, assigned_question_ids) {
  const loader = await ctx.show_loader('Loading existing questions...');

  try {
    const rows = await fetch_questions_in_project({ form_id: form_id });
    ctx.question_list = rows.map((question, index) => {
      const question_row = buildQuestionRow(ctx, question, index, assigned_question_ids);
      return question_row;
    });
  } catch (error) {
    ctx.question_list = [];
    ctx.show_error(extract_error_message(error, 'Failed to load questions.'));
  } finally {
    await ctx.close_loader(loader);
    ctx.is_loading_questions = false;
  }
}

export function resolveAssignmentToggleContext(ctx, question, resolveQuestionId, resolveSectionId) {
  const question_id = resolveQuestionId(question);
  const form_id = Number(ctx.topBar.formId);
  const section_id = resolveSectionId(ctx.active_assign_section);
  const is_valid = Boolean(question_id && form_id && section_id && !question.is_saving);
  const toggle_context = {
    form_id,
    is_valid,
    question_id,
    section_id,
  };
  return toggle_context;
}

export function canToggleExistingQuestionAssignment(ctx, question, toggle_context) {
  if (!toggle_context.is_valid) {
    return false;
  }

  if (question.is_dependent) {
    ctx.show_error('Dependent questions cannot be assigned directly.');
    return false;
  }

  return true;
}

export async function assignQuestionToSection(ctx, question, question_id, form_id, section_id) {
  const response = await axios.post(
    ASSIGN_QUESTIONS_TO_SECTION_URL,
    { question_ids: [question_id], form_id: form_id, section_id: section_id },
    get_request_config()
  );
  const parsed_response = ctx.parse_response_data(response);

  if (parsed_response?.status !== 'success') {
    ctx.show_error(ctx.normalize_error_message(parsed_response?.message, 'Failed to assign questions to section.'));
    return;
  }

  question.is_assigned = true;
  await ctx.load_form_sections();
  ctx.expand_section_by_id(section_id);
  ctx.show_success(parsed_response.message || 'Questions assigned to section successfully.');
}

export async function unassignQuestionFromSection(ctx, question, section_id) {
  const was_unassigned = await ctx.unassign_question(ctx.active_assign_section, question);
  if (was_unassigned) {
    question.is_assigned = false;
    ctx.expand_section_by_id(section_id);
  }
}

export async function toggleQuestionAssignment(ctx, question, toggle_context) {
  if (question.is_assigned) {
    await unassignQuestionFromSection(ctx, question, toggle_context.section_id);
    return;
  }

  await assignQuestionToSection(
    ctx,
    question,
    toggle_context.question_id,
    toggle_context.form_id,
    toggle_context.section_id
  );
}

export function findSectionById(sections, normalized_section_id, resolveSectionId) {
  const section = (Array.isArray(sections) ? sections : []).find((item) => {
    const item_section_id = resolveSectionId(item);
    const is_match = item_section_id === normalized_section_id;
    return is_match;
  }) || null;
  return section;
}

export function findQuestionInSection(ctx, section, question_id, section_question_id) {
  const matched_question = (Array.isArray(section?.questions) ? section.questions : []).find((question) => {
    const is_match = ctx.question_matches_assignment(question, question_id, section_question_id);
    return is_match;
  }) || null;
  return matched_question;
}

function resolveQuestionRef(section, question_ref) {
  const question = question_ref && typeof question_ref === 'object'
    ? question_ref
    : section?.questions?.[Number(question_ref)];
  return question;
}

function buildUnassignPayload(question_id, section_id, section_question_id, form_id) {
  const payload = {
    form_id,
    question_id,
    section_id,
    section_question_id,
  };
  return payload;
}

function resolveUnassignFormId(ctx) {
  const form_id = String(ctx.topBar.formId || ctx.$route?.query?.form_id || '').trim();
  return form_id;
}

export function resolveUnassignPayload(ctx, section_ref, question_ref, resolveSection, resolveQuestionId, resolveSectionId) {
  const section = resolveSection(ctx, section_ref);
  const question = resolveQuestionRef(section, question_ref);
  const question_id = resolveQuestionId(question);
  const section_question_id = resolveSectionQuestionId(question);
  const section_id = resolveSectionId(section);
  const form_id = resolveUnassignFormId(ctx);
  const has_required_fields = Boolean(question_id && form_id);

  if (!has_required_fields) {
    return null;
  }

  const payload = buildUnassignPayload(
    question_id,
    section_id,
    section_question_id,
    form_id
  );
  return payload;
}

export function setQuestionUnassigningState(ctx, payload, is_unassigning) {
  if (payload.section_id) {
    ctx.set_question_unassigning(payload.section_id, payload.question_id, is_unassigning);
  }
}

export async function performUnassignRequest(payload) {
  const response = await unassign_question_from_section({
    question_id: payload.question_id,
    question_ids: [payload.question_id],
    section_question_id: payload.section_question_id || undefined,
    section_question_ids: payload.section_question_id ? [payload.section_question_id] : undefined,
    section_id: payload.section_id || undefined,
    form_id: payload.form_id,
  });
  return response;
}

export function ensureUnassignSucceeded(response) {
  const response_status = String(response?.status || '').toLowerCase();
  if (response_status !== 'success') {
    throw new Error(response?.message || 'Failed to unassign question.');
  }
}

export function ensureQuestionRemoved(ctx, payload) {
  const existing_question = ctx.find_section_question(
    payload.section_id,
    payload.question_id,
    payload.section_question_id
  );
  if (existing_question) {
    throw new Error('Question was not unassigned. Please try again.');
  }
}

export async function finalizeSuccessfulUnassign(ctx, payload, response) {
  await ctx.load_form_sections();
  if (payload.section_id) {
    ctx.expand_section_by_id(payload.section_id);
  }
  ensureQuestionRemoved(ctx, payload);
  ctx.show_success(response.message || 'Question unassigned successfully');
}
